import { useCustomTranslation } from '../../../../../../../../hooks/useCustomTranslation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowTurnDownRight, faIndustryWindows } from '@fortawesome/pro-light-svg-icons';
import { getGridTemplateColumns } from '../../../../utils/gridTemplateColumns';
import { faFlag as faFlagSolid } from '@fortawesome/pro-solid-svg-icons';
import { ExtendedTaskNode, useGenerateTaskDependencyTree } from '../../../../hooks';
import { CommonLoader } from '../../../../../../../atoms/CommonLoader';
import clsx from 'clsx';
import { useDateFormatter } from '@nexelem/utils';
import { Variant } from './SimulationLotMain';
const borderByVariant: Record<Variant, string> = {
  default: '',
  priority: 'border-x-border-brand-lighter last:border-b-border-brand-lighter',
};

type SimulationLotTreeItemProps = {
  childTask: ExtendedTaskNode;
  depth?: number;
  index: number;
  batchNumber: string;
  variant: Variant;
};

const SimulationLotTreeItem = ({ childTask, depth = 1, index, batchNumber, variant }: SimulationLotTreeItemProps) => {
  const { tWrapper } = useCustomTranslation('products.simulationPlan');
  const formatDate = useDateFormatter({ displayWithHours: true });

  const orderNumber =
    childTask.qapsSimulationData?.productionOrderIdentifier ||
    childTask.productionOrderDetails?.externalId ||
    childTask.productionOrderDetails?.number ||
    '-';

  return (
    <>
      <div
        style={getGridTemplateColumns(depth)}
        className={clsx(
          'grid items-center gap-2 border border-t-0 border-border-container bg-surface-container p-2 last:rounded-b-xs',
          borderByVariant[variant],
        )}
      >
        {/* Grid Column 1 */}
        <div className="flex items-center justify-end gap-2">
          {variant === 'priority' && (
            <div className="mr-auto flex h-8.5 w-8 items-center justify-center">
              <FontAwesomeIcon className="min-h-5 w-5 text-icon-brand opacity-50" icon={faFlagSolid} />
            </div>
          )}
          <FontAwesomeIcon className="min-h-5 w-5 p-2.5" icon={faArrowTurnDownRight} />
          <FontAwesomeIcon
            className="min-h-5 w-5 rounded-full border border-border-container bg-surface-container-lighter p-1"
            icon={faIndustryWindows}
          />
        </div>

        {/* Grid Column 2 */}
        <div className="flex flex-col gap-0.5">
          <p className="inline-flex gap-2 text-s11-sb uppercase">
            {tWrapper('taskItemProductNumber')} <span>{childTask.productDetails?.code?.split(' ')?.[0] ?? '-'}</span>
          </p>
          <p className="text-s16-sb">{childTask.productDetails.name}</p>
          <p className="inline-flex gap-2 text-s11-sb uppercase">
            <span className="text-text-status-green">
              {tWrapper('taskItemBatchNumber')} {batchNumber}
            </span>
            <span className="rounded-xs border border-border-primary px-1 text-text-secondary-darker">
              {tWrapper('taskItemOrderNumber')} {orderNumber}
            </span>
          </p>
        </div>

        {/* Grid Column 3 */}
        <div className="flex items-center justify-end">
          <span className="pr-2 text-s18">{childTask.progress?.totalAmount?.amount ?? '-'}</span>
        </div>

        {/* Grid Column 4 */}
        <div className="text-s18 text-text-secondary-darker">{childTask.progress?.totalAmount?.unit?.name ?? '-'}</div>

        {/* Grid Column 5 */}
        <div className="flex flex-col gap-0.5">
          <p className="inline-flex gap-2 text-s11-sb text-text-secondary-darker uppercase">
            <span>{tWrapper('taskItemArea')}</span>
            <span>{childTask.queue?.area?.name || '-'}</span>
          </p>
          <span className="text-s16">{childTask.queue?.shortName || '-'}</span>
        </div>

        {/* Grid Column 7 */}
        <div>
          <span className="text-s16">{childTask.qapsSimulationData?.recipeVersion || '-'}</span>
        </div>

        {/* Grid Column 8 */}
        <div></div>

        {/* Grid Column 9 */}
        <div className="text-s18">{formatDate(childTask.start)}</div>
      </div>

      {childTask.children && childTask.children.length > 0 && (
        <>
          {childTask.children.map((child, childIndex) => (
            <SimulationLotTreeItem
              batchNumber={`${batchNumber}.${childIndex + 1}`}
              childTask={child}
              depth={depth + 1}
              index={index}
              key={child.id}
              variant={variant}
            />
          ))}
        </>
      )}
    </>
  );
};

type SimulationLotTreeProps = {
  taskId: number;
  index: number;
  variant: Variant;
};

export const SimulationLotTree = ({ taskId, index, variant }: SimulationLotTreeProps) => {
  const { data: childrenTasks, isLoading } = useGenerateTaskDependencyTree(taskId.toString());

  if (isLoading) {
    return (
      <div className="border-b border-border-container bg-surface-container">
        <CommonLoader height="75px" size={40} />
      </div>
    );
  }

  return (
    <>
      {childrenTasks?.map((child, childIndex) => (
        <SimulationLotTreeItem
          batchNumber={`${index + 1}.${childIndex + 1}`}
          childTask={child}
          index={index}
          key={child.id}
          variant={variant}
        />
      ))}
    </>
  );
};
